import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';

type CostColumnHeaderProps = {
    /** Whose stock the figures count. */
    stockFigures: StockFiguresScope;
};

/**
 * The Cost column's header: its label and an info button whose tooltip says
 * what the number is (BOS-200). Current cost is computed, not stored: it is
 * the purchase price of the next batch to sell, so it moves as stock sells
 * down, and without the tooltip it reads as an average.
 *
 * The tooltip names the scope (bos-app#118): the next batch in one branch, or
 * across all branches the oldest batch in stock anywhere, which is a
 * different number.
 *
 * The button is named by a visually hidden span, not `aria-label`: HeroUI's
 * Tooltip drops an `aria-label` from its trigger. A keyboard user arrows onto
 * the Cost column header and presses Tab to reach it; the tooltip opens on
 * that focus.
 */
export const CostColumnHeader: React.FC<CostColumnHeaderProps> = ({
    stockFigures,
}) => {
    const { t } = useTranslation('pages');

    const body = (() => {
        switch (stockFigures.kind) {
            case 'allBranches':
                return t('products.components.productTable.costHint.body');
            case 'branch':
                return t(
                    'products.components.productTable.costHint.bodyBranch',
                    {
                        branch: stockFigures.name,
                    },
                );
            case 'unnamedBranch':
                return t(
                    'products.components.productTable.costHint.bodyUnnamedBranch',
                );
        }
    })();

    return (
        <span className="inline-flex items-center gap-1">
            {t('products.components.productTable.columns.cost')}
            <Tooltip delay={0}>
                <Button
                    isIconOnly
                    size="sm"
                    variant="ghost"
                    {...getNodeTestIdProp('product-table-cost-hint')}
                >
                    <Icon aria-hidden icon="gravity-ui:circle-info" />
                    <span className="sr-only">
                        {t('products.components.productTable.costHint.name')}
                    </span>
                </Button>
                <Tooltip.Content>
                    <div className="flex max-w-xs flex-col gap-0.5">
                        <Typography type="body-xs" weight="semibold">
                            {t(
                                'products.components.productTable.costHint.title',
                            )}
                        </Typography>
                        <Typography
                            type="body-xs"
                            {...getNodeTestIdProp(
                                'product-table-cost-hint-body',
                            )}
                        >
                            {body}
                        </Typography>
                    </div>
                </Tooltip.Content>
            </Tooltip>
        </span>
    );
};
